import { queryOptions, skipToken, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/http.ts";
import type {
  GetConnectorPromptPicksResp,
  GetConnectorPromptResp,
} from "@/types/connectorPrompt.ts";

/**
 * Whether the connector prompt is due, asked once per app load.
 *
 * `staleTime: Infinity` and no refetch on focus: the server answers from when
 * the prompt last showed, so asking again after it shows would only ever say
 * no, and asking again while it is open must not close it. A failed request
 * (including an API that predates the endpoint) reads as not due.
 */
export const connectorPromptQuery = queryOptions({
  queryKey: ["connectorPrompt"] as const,
  queryFn: async ({ signal }) =>
    (await api.get<GetConnectorPromptResp>("/api/a/connector-prompt", { signal })).data,
  staleTime: Number.POSITIVE_INFINITY,
  refetchOnWindowFocus: false,
  retry: false,
});

/** Connectors recommended for this person, scored when the prompt opens. */
export const connectorPromptPicksQuery = (enabled: boolean) =>
  queryOptions({
    queryKey: ["connectorPrompt", "picks"] as const,
    queryFn: enabled
      ? async ({ signal }) =>
          (
            await api.get<GetConnectorPromptPicksResp>("/api/a/connector-prompt/picks", {
              params: { platform: "web" },
              signal,
            })
          ).data
      : skipToken,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    retry: false,
  });

/**
 * Record that the prompt showed, which starts its wait before the next showing.
 * The cached answer becomes not due at once, as the server's will, so neither a
 * remount nor a refetch racing the POST can show the prompt a second time.
 */
export const useRecordConnectorPromptShown = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post("/api/a/connector-prompt/shown", {});
    },
    onMutate: async () => {
      // A status read already in flight could land after this and say due again.
      await queryClient.cancelQueries({ queryKey: connectorPromptQuery.queryKey, exact: true });
      queryClient.setQueryData<GetConnectorPromptResp>(connectorPromptQuery.queryKey, {
        due: false,
      });
    },
    // Again once the server has it, over any read that started in between.
    onSuccess: () => {
      queryClient.setQueryData<GetConnectorPromptResp>(connectorPromptQuery.queryKey, {
        due: false,
      });
    },
  });
};
